import * as t from 'io-ts';

/**
 * A process the signed-in person started: a report, invoice run or export they can leave running.
 *
 * Mirrors `com.wm.service.process.UserProcessView` (`/processes`). The server leaves out null fields.
 */
export const UserProcessResultFileCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    fileName: t.string,
  }),
  t.partial({
    contentType: t.string,
    contentLength: t.number,
    /** Signed and short-lived; reload the process for a fresh one. */
    downloadUrl: t.string,
  }),
]);

export type UserProcessResultFile = t.TypeOf<typeof UserProcessResultFileCodec>;

/** A fact about the input, shown as a chip: kind is "category", "region", "company" or "flag". */
export const UserProcessTagCodec = t.intersection([
  t.type({
    kind: t.string,
    label: t.string,
  }),
  t.partial({
    /** A machine value where one helps, e.g. the category code WATER. */
    value: t.string,
  }),
]);

export type UserProcessTag = t.TypeOf<typeof UserProcessTagCodec>;

export const UserProcessCodec = t.intersection([
  t.type({
    id: t.string,
    processKey: t.string,
    name: t.string,
    type: t.string,
    status: t.string,
    successCount: t.number,
    warningCount: t.number,
    failureCount: t.number,
    hasDiagnostics: t.boolean,
    cancelRequested: t.boolean,
    notifyEmail: t.boolean,
    seen: t.boolean,
    files: t.array(UserProcessResultFileCodec),
  }),
  t.partial({
    /** The main line for the input, e.g. "Sep 1, 2026 – Sep 30, 2026". */
    description: t.string,
    /** Chips for the rest of the input: region, product, company, options. */
    tags: t.array(UserProcessTagCodec),
    /** Structured message (RichMessage) explaining a failure, when it had one. */
    failureMessage: t.unknown,
    /** URLs the waiting endpoint returns for the same work, for callers that relied on them. */
    resultUrls: t.array(t.string),
    input: t.unknown,
    /** Current step while queued or running, e.g. "Building report". */
    step: t.string,
    progressDone: t.number,
    progressTotal: t.number,
    /** Outcome for finished processes, or the reason for a failure. */
    summary: t.string,
    /** WATER, OIL, or absent. */
    category: t.string,
    createDttm: t.number,
    startDttm: t.number,
    endDttm: t.number,
  }),
]);

export type UserProcess = t.TypeOf<typeof UserProcessCodec>;

export type UserProcessStatusCode =
  | 'QUEUED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'PARTIAL'
  | 'FAILED'
  | 'CANCELED';

export const isUserProcessActive = (process: Pick<UserProcess, 'status'>): boolean =>
  process.status === 'QUEUED' || process.status === 'RUNNING';

/** Finished cleanly but had nothing to report: no records and nothing to download. */
export const isUserProcessNoRecords = (process: Pick<UserProcess, 'status' | 'successCount' | 'files'>): boolean =>
  process.status === 'SUCCEEDED' && process.successCount === 0 && process.files.length === 0;

/** Body for `POST /processes/{processKey}`. */
export type SubmitUserProcessArgs<I = unknown> = {
  processKey: string;
  input: I;
  /** Email the person when it finishes. */
  notifyEmail?: boolean;
};
